/**
 * Admin Dashboard Page
 * Overview of system metrics and recent activity
 */

import React from 'react';
import { Link } from 'react-router-dom';
import { BaseLayout } from '@/layouts';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { 
  LayoutGrid, 
  FileText, 
  Users, 
  Clock, 
  CheckCircle2, 
  XCircle,
  TrendingUp,
  Activity,
  ArrowRight,
  FolderOpen,
  Plus,
} from 'lucide-react';
import { useBEStudy } from '@/hooks/useBEStudy';
import { useDocuments } from '@/hooks/useDocuments';
import { useTemplates } from '@/hooks/useTemplates';
import { ROUTES } from '@/constants/routes';
import { formatDate, getRelativeTime } from '@/constants/beStudyMockData';
import { cn } from '@/lib/utils';

/**
 * Metric Card Component
 */
function MetricCard({ title, value, description, icon: Icon, trend, trendValue, color = 'primary' }) {
  const colorClasses = {
    primary: 'bg-primary/10 text-primary',
    blue: 'bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400',
    green: 'bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400',
    amber: 'bg-amber-100 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400',
    red: 'bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400',
    purple: 'bg-purple-100 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400',
  };

  return (
    <Card data-testid={`metric-card-${title.toLowerCase().replace(/\s+/g, '-')}`}>
      <CardContent className="p-4">
        <div className="flex items-start justify-between">
          <div>
            <p className="text-sm font-medium text-muted-foreground">{title}</p>
            <p className="text-3xl font-bold mt-2">{value}</p>
            {description && (
              <p className="text-xs text-muted-foreground mt-1">{description}</p>
            )}
            {trend && (
              <div className={cn(
                'flex items-center gap-1 mt-2 text-xs',
                trend === 'up' ? 'text-green-600' : 'text-red-600'
              )}>
                <TrendingUp className={cn('h-3 w-3', trend === 'down' && 'rotate-180')} />
                <span>{trendValue}</span>
              </div>
            )}
          </div>
          <div className={cn('p-3 rounded-lg', colorClasses[color])}>
            <Icon className="h-6 w-6" />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

/**
 * Activity Item Component
 */
function ActivityItem({ activity }) {
  const actionIcons = {
    'Created Document': FileText,
    'Updated Document': FileText,
    'Added Comment': Activity,
    'Started Review': Clock,
    'Approved Document': CheckCircle2,
    'Rejected Document': XCircle,
    'Updated Template': FolderOpen,
  };

  const Icon = actionIcons[activity.action] || Activity;

  return (
    <div className="flex items-start gap-3 py-2" data-testid={`activity-item-${activity.id}`}>
      <div className="p-2 rounded-full bg-muted">
        <Icon className="h-4 w-4 text-muted-foreground" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm">
          <span className="font-medium">{activity.user}</span>
          {' '}
          <span className="text-muted-foreground">{activity.action.toLowerCase()}</span>
        </p>
        <p className="text-sm text-primary truncate">{activity.target}</p>
        <p className="text-xs text-muted-foreground mt-0.5">
          {getRelativeTime(activity.timestamp)}
        </p>
      </div>
    </div>
  );
}

/**
 * Document Status Chart (Simple Bar)
 */
function DocumentStatusChart({ data }) {
  const total = data.reduce((sum, item) => sum + item.count, 0);

  return (
    <div className="space-y-3">
      {data.map((item) => (
        <div key={item.status} className="space-y-1">
          <div className="flex items-center justify-between text-sm">
            <span>{item.status}</span>
            <span className="font-medium">{item.count}</span>
          </div>
          <div className="h-2 rounded-full bg-muted overflow-hidden">
            <div
              className="h-full rounded-full transition-all duration-500"
              style={{
                width: `${total > 0 ? (item.count / total) * 100 : 0}%`,
                backgroundColor: item.color,
              }}
            />
          </div>
        </div>
      ))}
    </div>
  );
}

/**
 * Admin Dashboard Page Component
 */
export function AdminDashboard() {
  const { getAdminDashboard, userStatistics } = useBEStudy();
  const { statistics: docStats } = useDocuments();
  const { templates } = useTemplates();

  const dashboard = getAdminDashboard();

  return (
    <BaseLayout>
      <div className="space-y-4" data-testid="admin-dashboard">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold">Admin Dashboard</h1>
            <p className="text-muted-foreground">
              Overview of system activity and metrics
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" asChild>
              <Link to={ROUTES.TEMPLATES}>
                <FolderOpen className="h-4 w-4 mr-2" />
                Templates
              </Link>
            </Button>
            <Button asChild>
              <Link to={ROUTES.DOCUMENTS}>
                <Plus className="h-4 w-4 mr-2" />
                New Document
              </Link>
            </Button>
          </div>
        </div>

        {/* Metrics Grid */}
        <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-4">
          <MetricCard
            title="Total Templates"
            value={templates.length}
            description={`${templates.filter(t => t.status === 'ACTIVE').length} active`}
            icon={FolderOpen}
            color="purple"
          />
          <MetricCard
            title="Total Documents"
            value={docStats.total}
            description={`${docStats.inProgress + docStats.draft} in progress`}
            icon={FileText}
            color="blue"
          />
          <MetricCard
            title="Pending Reviews"
            value={docStats.pendingReview + docStats.inReview}
            description="Awaiting reviewer action"
            icon={Clock}
            color="amber"
          />
          <MetricCard
            title="Approved"
            value={docStats.approved}
            description={`${docStats.rejected} rejected`}
            icon={CheckCircle2}
            color="green"
          />
        </div>

        {/* Secondary Metrics */}
        <div className="grid gap-3 md:grid-cols-3">
          <MetricCard
            title="Total Users"
            value={userStatistics.total}
            description={`${userStatistics.active} active`}
            icon={Users}
            color="primary"
          />
          <MetricCard
            title="Admins"
            value={userStatistics.admins}
            icon={LayoutGrid}
            color="red"
          />
          <MetricCard
            title="Reviewers"
            value={userStatistics.reviewers}
            description={`${userStatistics.regularUsers} regular users`}
            icon={Users}
            color="purple"
          />
        </div>

        {/* Charts and Activity */}
        <div className="grid gap-4 lg:grid-cols-2">
          {/* Document Status Distribution */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Documents by Status</CardTitle>
              <CardDescription>Distribution of document statuses</CardDescription>
            </CardHeader>
            <CardContent>
              <DocumentStatusChart data={dashboard.documentsByStatus} />
            </CardContent>
          </Card>

          {/* Recent Activity */}
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle className="text-lg">Recent Activity</CardTitle>
                <CardDescription>Latest actions in the system</CardDescription>
              </div>
              <Button variant="ghost" size="sm" className="text-primary">
                View All
                <ArrowRight className="h-4 w-4 ml-1" />
              </Button>
            </CardHeader>
            <CardContent>
              <ScrollArea className="h-[300px] pr-4">
                <div className="divide-y">
                  {dashboard.recentActivity.map((activity) => (
                    <ActivityItem key={activity.id} activity={activity} />
                  ))}
                </div>
              </ScrollArea>
            </CardContent>
          </Card>
        </div>

        {/* Quick Links */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Quick Actions</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
              <Button variant="outline" className="h-auto py-4 flex-col" asChild>
                <Link to={ROUTES.TEMPLATES}>
                  <FolderOpen className="h-6 w-6 mb-2" />
                  <span>Manage Templates</span>
                </Link>
              </Button>
              <Button variant="outline" className="h-auto py-4 flex-col" asChild>
                <Link to={ROUTES.USER_MANAGEMENT}>
                  <Users className="h-6 w-6 mb-2" />
                  <span>Manage Users</span>
                </Link>
              </Button>
              <Button variant="outline" className="h-auto py-4 flex-col" asChild>
                <Link to={ROUTES.PROMPTS}>
                  <Activity className="h-6 w-6 mb-2" />
                  <span>AI Prompts</span>
                </Link>
              </Button>
              <Button variant="outline" className="h-auto py-4 flex-col" asChild>
                <Link to={ROUTES.CONFIG}>
                  <LayoutGrid className="h-6 w-6 mb-2" />
                  <span>Configuration</span>
                </Link>
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </BaseLayout>
  );
}

export default AdminDashboard;
